import { isValidTimeZone } from "../time";
// Finance Config validation: checks a stored config against the native module
// shape documented in normalizeConfig.js and returns a list of problems, each
// naming its path. Read-only: it never rewrites a config. A legacy flat config
// is checked after normalizeConfig adapts it, since that is what renders.
import normalizeConfig from "./normalizeConfig";

const MODULES = ["retainer", "recurringSessions", "drawdownPool", "rateCard", "sessionBlock"];
const CURRENCIES = ["NZD", "USD", "GBP", "AUD"];
const TAX = ["gst", "none"];
const ROSTER_STATUS = ["active", "paused", "optedOut", "ended"];
const INVOICE_STATUS = ["paid", "sent", "invoiced", "part", "pending"]; // what InvoicePill renders
const MODES = ["sessions", "seats"];
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

const isObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

export default function validateConfig(raw) {
  if (!isObj(raw)) return ["Finance Config is not an object"];
  const problems = [];
  const say = (msg) => problems.push(msg);
  const has = (o, k) => o[k] !== undefined && o[k] !== null;
  const num = (o, k, path) => { if (has(o, k) && !Number.isFinite(o[k])) say(`${path}.${k} is not a number`); };
  const month = (o, k, path) => { if (has(o, k) && !MONTH.test(o[k])) say(`${path}.${k} "${o[k]}" is not YYYY-MM`); };
  const oneOf = (o, k, path, allowed) => { if (has(o, k) && !allowed.includes(o[k])) say(`${path ? path + "." : ""}${k} "${o[k]}" is not one of ${allowed.join(", ")}`); };
  const list = (o, k, path, each) => {
    if (!has(o, k)) return;
    if (!Array.isArray(o[k])) { say(`${path}.${k} is not a list`); return; }
    o[k].forEach((item, i) => { const p = `${path}.${k}[${i}]`; if (!isObj(item)) say(`${p} is not an object`); else if (each) each(item, p); });
  };
  const invoice = (inv, p) => { month(inv, "month", p); num(inv, "amount", p); num(inv, "sessions", p); oneOf(inv, "status", p, INVOICE_STATUS); list(inv, "lines", p, (l, lp) => num(l, "amount", lp)); };

  let modules = raw.modules;
  if (modules === undefined) modules = normalizeConfig(raw).modules; // legacy flat shape
  else {
    oneOf(raw, "currency", "", CURRENCIES);
    oneOf(raw, "tax", "", TAX);
    if (has(raw, "billing")) {
      if (!isObj(raw.billing)) say("billing is not an object");
      else { oneOf(raw.billing, "region", "billing", ["nz", "international"]); oneOf(raw.billing, "invoicing", "billing", ["xero", "hub"]); }
    }
    if (has(raw, "timeZone") && !isValidTimeZone(raw.timeZone)) say(`timeZone "${raw.timeZone}" is not a time zone (use an IANA name like America/Chicago)`);
    month(raw, "engagementStart", "config");
    num(raw, "ledgerLookAhead", "config");
    if (!isObj(modules)) return [...problems, "modules is not an object"];
  }

  Object.keys(modules).filter(k => !MODULES.includes(k)).forEach(k => say(`Unknown module "${k}"`));
  MODULES.filter(k => modules[k] !== undefined).forEach(k => {
    const m = modules[k];
    const path = `modules.${k}`;
    if (!isObj(m)) { say(`${path} is not an object`); return; }
    if (k === "retainer") { num(m, "amount", path); num(m, "monthlyTotal", path); list(m, "includes", path); list(m, "invoices", path, invoice); }
    if (k === "recurringSessions") {
      num(m, "rolloverValue", path);
      oneOf(m, "mode", path, MODES);
      list(m, "seats", path, (s, p) => { num(s, "count", p); num(s, "rate", p); });
      list(m, "sessions", path, (s, p) => { oneOf(s, "status", p, ROSTER_STATUS); month(s, "from", p); month(s, "to", p); month(s, "started", p); num(s, "rate", p); });
    }
    if (k === "drawdownPool") { num(m, "monthlyAccrual", path); num(m, "openingBalance", path); }
    if (k === "rateCard") list(m, "rates", path, (r, p) => num(r, "rate", p));
    if (k === "sessionBlock") { ["purchased", "blockNo", "rate", "value"].forEach(f => num(m, f, path)); list(m, "invoices", path, invoice); }
  });
  return problems;
}

// For a raw Airtable cell: empty is fine (no finance configured), unparseable
// is one problem.
export function storedConfigProblems(cell) {
  if (!cell) return [];
  let parsed;
  try { parsed = JSON.parse(cell); } catch (e) { return ["Finance Config is not valid JSON"]; }
  return validateConfig(parsed);
}
